import { useState } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import {
  LayoutDashboard, Building2, Package, CreditCard, Palette, FileText, ArrowLeft, LogOut, Mail, ShieldAlert, ShieldCheck, Menu, X, UserCheck, Wrench, Database, Sparkles, Clock3, Chrome, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/super-admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "admin-nav-overview" },
  { to: "/super-admin/workspaces", label: "Workspaces", icon: Building2, testid: "admin-nav-workspaces" },
  { to: "/super-admin/ai-usage", label: "AI Usage & Pricing", icon: Sparkles, testid: "admin-nav-ai-usage" },
  { to: "/super-admin/waitlist", label: "Waitlist & Users", icon: UserCheck, testid: "admin-nav-waitlist" },
  { to: "/super-admin/plans", label: "Plans & Pricing", icon: Package, testid: "admin-nav-plans" },
  { to: "/super-admin/payment-gateway", label: "Payment Gateway", icon: CreditCard, testid: "admin-nav-gateway" },
  { to: "/super-admin/email", label: "Email Provider", icon: Mail, testid: "admin-nav-email" },
  { to: "/super-admin/branding", label: "Platform Branding", icon: Palette, testid: "admin-nav-branding" },
  { to: "/super-admin/legal", label: "Legal Pages", icon: FileText, testid: "admin-nav-legal" },
  { to: "/super-admin/audit", label: "Audit Log", icon: ShieldAlert, testid: "admin-nav-audit" },
  { to: "/super-admin/security", label: "Security (2FA)", icon: ShieldCheck, testid: "admin-nav-security" },
  { to: "/super-admin/maintenance", label: "System Maintenance", icon: Wrench, testid: "admin-nav-maintenance" },
  { to: "/super-admin/data-integrity", label: "Data Integrity", icon: Database, testid: "admin-nav-data-integrity" },
  { to: "/super-admin/perf-stats", label: "API perf", icon: Activity, testid: "admin-nav-perf-stats" },
  { to: "/super-admin/chrome-extension", label: "Chrome Extension", icon: Chrome, testid: "admin-nav-chrome-extension" },
  // R76 · Scheduler ops lives in the workspace app shell (needs auth ctx) — link out.
  { to: "/app/settings/scheduler", label: "Scheduler ops", icon: Clock3, testid: "admin-nav-scheduler" },
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const { branding } = useTheme();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="workspace-shell">
      {drawerOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setDrawerOpen(false)} data-testid="admin-sidebar-backdrop" />
      )}
      <aside className={`workspace-sidebar border-r border-border bg-foreground text-background flex-col z-50 ${drawerOpen ? "flex" : "hidden"} md:flex`} data-testid="admin-sidebar">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/super-admin" onClick={() => setDrawerOpen(false)} className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black overflow-hidden">
              {branding.logo_icon_url ? (
                <img src={branding.logo_icon_url} alt={branding.platform_name} className="w-full h-full object-cover" />
              ) : branding.logo_url ? (
                <img src={branding.logo_url} alt={branding.platform_name} className="w-full h-full object-cover" />
              ) : (
                (branding.platform_name || "A").slice(0, 1)
              )}
            </div>
            <div>
              <div className="font-bold leading-tight" style={{ fontFamily: "var(--font-heading)" }}>{branding.platform_name}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-60">Super Admin</div>
            </div>
          </Link>
          <button type="button" onClick={() => setDrawerOpen(false)} className="md:hidden text-background/70 hover:text-background" data-testid="admin-sidebar-close-btn" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-1 overflow-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              onClick={() => setDrawerOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition ${
                  isActive ? "bg-primary text-primary-foreground font-medium" : "hover:bg-white/5 text-background/80"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3 space-y-2">
          <Button onClick={() => nav("/app")} variant="outline" size="sm" className="w-full rounded-none border-white/20 bg-transparent hover:bg-white/10 text-background" data-testid="admin-exit-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Exit to app
          </Button>
          <Button onClick={logout} variant="ghost" size="sm" className="w-full rounded-none text-background hover:bg-white/10 truncate" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2 shrink-0" /> <span className="truncate">Log out · {user?.email}</span>
          </Button>
        </div>
      </aside>
      <main className="min-w-0 bg-background">
        {/* Mobile top bar with hamburger */}
        <div className="md:hidden border-b border-border bg-background px-4 h-12 flex items-center gap-2 sticky top-0 z-30">
          <button type="button" onClick={() => setDrawerOpen(true)} className="text-foreground/60 hover:text-foreground p-1" data-testid="admin-sidebar-open-btn" aria-label="Open admin menu">
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-bold text-sm" style={{ fontFamily: "var(--font-heading)" }}>Super Admin</span>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
